import React, { useEffect, useState, useMemo } from 'react';
import { landlordAPI } from '../../../lib/api';
import { formatCurrency } from '../../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  BarChart3, TrendingUp, TrendingDown, Home, Calendar, DollarSign,
  PieChart as PieChartIcon, ArrowUp, ArrowDown, Minus
} from 'lucide-react';

const ChangeIndicator = ({ value }) => {
  if (value > 0) return <span className="text-green-600 flex items-center text-sm"><ArrowUp className="w-3 h-3 mr-1" />{value.toFixed(1)}%</span>;
  if (value < 0) return <span className="text-red-600 flex items-center text-sm"><ArrowDown className="w-3 h-3 mr-1" />{Math.abs(value).toFixed(1)}%</span>;
  return <span className="text-muted-foreground flex items-center text-sm"><Minus className="w-3 h-3 mr-1" />0%</span>;
};

export const ReportsAnalyticsTab = () => {
  const [timeRange, setTimeRange] = useState('6months');
  const [analyticsData, setAnalyticsData] = useState({
    properties: [], units: [], leases: [], payments: [], expenses: []
  });

  useEffect(() => {
    (async () => {
      try {
        const [propsRes, unitsRes, leasesRes, paymentsRes, expensesRes] = await Promise.all([
          landlordAPI.getProperties(),
          landlordAPI.getUnits(),
          landlordAPI.getLeases(),
          landlordAPI.getPayments(),
          landlordAPI.getExpenses()
        ]);
        setAnalyticsData({
          properties: propsRes.data,
          units: unitsRes.data,
          leases: leasesRes.data,
          payments: paymentsRes.data,
          expenses: expensesRes.data
        });
      } catch (err) {
        console.error('Error loading analytics data:', err);
      }
    })();
  }, []);

  const analytics = useMemo(() => {
    const now = new Date();
    const monthsAgo = (months) => {
      const d = new Date();
      d.setMonth(d.getMonth() - months);
      return d;
    };

    const rangeMonths = timeRange === '1month' ? 1 : timeRange === '3months' ? 3 : timeRange === '6months' ? 6 : 12;
    const startDate = monthsAgo(rangeMonths);

    const verifiedPayments = analyticsData.payments.filter(p => p.status === 'verified');
    const periodPayments = verifiedPayments.filter(p => new Date(p.created_at) >= startDate);
    const totalRevenue = periodPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

    const prevStartDate = monthsAgo(rangeMonths * 2);
    const prevPeriodPayments = verifiedPayments.filter(p => {
      const d = new Date(p.created_at);
      return d >= prevStartDate && d < startDate;
    });
    const prevRevenue = prevPeriodPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const revenueChange = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue * 100) : 0;

    const periodExpenses = analyticsData.expenses.filter(e => new Date(e.date || e.created_at) >= startDate);
    const totalExpenses = periodExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

    const prevPeriodExpenses = analyticsData.expenses.filter(e => {
      const d = new Date(e.date || e.created_at);
      return d >= prevStartDate && d < startDate;
    });
    const prevExpenses = prevPeriodExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const expenseChange = prevExpenses > 0 ? ((totalExpenses - prevExpenses) / prevExpenses * 100) : 0;

    const netIncome = totalRevenue - totalExpenses;
    const prevNetIncome = prevRevenue - prevExpenses;
    const netIncomeChange = prevNetIncome !== 0 ? ((netIncome - prevNetIncome) / Math.abs(prevNetIncome) * 100) : 0;

    const totalUnits = analyticsData.units.length;
    const occupiedUnits = analyticsData.units.filter(u => u.status === 'occupied').length;
    const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits * 100) : 0;

    const activeLeases = analyticsData.leases.filter(l => l.status === 'active').length;
    const pendingLeases = analyticsData.leases.filter(l => l.status === 'pending').length;

    const monthlyRevenue = [];
    for (let i = 5; i >= 0; i--) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthPayments = verifiedPayments.filter(p => {
        const d = new Date(p.created_at);
        return d >= monthStart && d <= monthEnd;
      });
      const monthExpenses = analyticsData.expenses.filter(e => {
        const d = new Date(e.date || e.created_at);
        return d >= monthStart && d <= monthEnd;
      });
      monthlyRevenue.push({
        month: monthStart.toLocaleDateString('en-US', { month: 'short' }),
        revenue: monthPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
        expenses: monthExpenses.reduce((sum, e) => sum + (e.amount || 0), 0)
      });
    }

    const revenueByProperty = analyticsData.properties.map(prop => {
      const propLeases = analyticsData.leases.filter(l => l.property_id === prop.property_id);
      const propPayments = verifiedPayments.filter(p =>
        propLeases.some(l => l.lease_id === p.lease_id)
      );
      return {
        name: prop.name,
        revenue: propPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
        units: analyticsData.units.filter(u => u.property_id === prop.property_id).length,
        occupied: analyticsData.units.filter(u => u.property_id === prop.property_id && u.status === 'occupied').length
      };
    });

    const unitStatus = {
      occupied: analyticsData.units.filter(u => u.status === 'occupied').length,
      vacant: analyticsData.units.filter(u => u.status === 'vacant').length,
      maintenance: analyticsData.units.filter(u => u.status === 'maintenance').length
    };

    const paymentStatus = {
      verified: verifiedPayments.length,
      pending: analyticsData.payments.filter(p => p.status === 'pending').length,
      rejected: analyticsData.payments.filter(p => p.status === 'rejected').length
    };

    const avgRent = analyticsData.leases.length > 0
      ? analyticsData.leases.reduce((sum, l) => sum + (l.monthly_rent || l.total_monthly_rent || 0), 0) / analyticsData.leases.length
      : 0;

    const monthlyAvgRevenue = totalRevenue / rangeMonths;
    const projectedAnnualRevenue = monthlyAvgRevenue * 12;

    return {
      totalRevenue, revenueChange, totalExpenses, expenseChange,
      netIncome, netIncomeChange, occupancyRate, totalUnits,
      occupiedUnits, activeLeases, pendingLeases, monthlyRevenue,
      revenueByProperty, unitStatus, paymentStatus, avgRent, projectedAnnualRevenue
    };
  }, [analyticsData, timeRange]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">Detailed insights into your property performance</p>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-40" data-testid="analytics-time-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1month">Last Month</SelectItem>
            <SelectItem value="3months">Last 3 Months</SelectItem>
            <SelectItem value="6months">Last 6 Months</SelectItem>
            <SelectItem value="12months">Last 12 Months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="w-4 h-4" /> Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(analytics.totalRevenue)}</div>
            <ChangeIndicator value={analytics.revenueChange} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Total Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(analytics.totalExpenses)}</div>
            <ChangeIndicator value={-analytics.expenseChange} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Net Income
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${analytics.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(analytics.netIncome)}
            </div>
            <ChangeIndicator value={analytics.netIncomeChange} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Home className="w-4 h-4" /> Occupancy Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.occupancyRate.toFixed(1)}%</div>
            <p className="text-sm text-muted-foreground">{analytics.occupiedUnits} of {analytics.totalUnits} units</p>
          </CardContent>
        </Card>
      </div>

      {/* Projected Revenue & Avg Rent */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#C85A42]" /> Projected Annual Revenue
            </CardTitle>
            <CardDescription>Based on current performance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#C85A42]">{formatCurrency(analytics.projectedAnnualRevenue)}</div>
            <p className="text-sm text-muted-foreground mt-2">Average monthly: {formatCurrency(analytics.projectedAnnualRevenue / 12)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#C85A42]" /> Average Rent
            </CardTitle>
            <CardDescription>Per lease agreement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(analytics.avgRent)}</div>
            <p className="text-sm text-muted-foreground mt-2">Across {analytics.activeLeases} active leases</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#C85A42]" /> Monthly Revenue vs Expenses
          </CardTitle>
          <CardDescription>Last 6 months breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.monthlyRevenue.map((month, i) => (
              <div key={month.month || `month-${i}`} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{month.month}</span>
                  <span className="text-muted-foreground">
                    Net: <span className={month.revenue - month.expenses >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(month.revenue - month.expenses)}
                    </span>
                  </span>
                </div>
                <div className="flex gap-2 h-6">
                  <div
                    className="bg-green-500 rounded-sm"
                    style={{ width: `${Math.max(5, (month.revenue / Math.max(...analytics.monthlyRevenue.map(m => m.revenue || 1))) * 50)}%` }}
                    title={`Revenue: ${formatCurrency(month.revenue)}`}
                  />
                  <div
                    className="bg-red-400 rounded-sm"
                    style={{ width: `${Math.max(2, (month.expenses / Math.max(...analytics.monthlyRevenue.map(m => m.revenue || 1))) * 50)}%` }}
                    title={`Expenses: ${formatCurrency(month.expenses)}`}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Revenue: {formatCurrency(month.revenue)}</span>
                  <span>Expenses: {formatCurrency(month.expenses)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-sm" />
              <span className="text-sm">Revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-400 rounded-sm" />
              <span className="text-sm">Expenses</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Property Performance & Status */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-[#C85A42]" /> Property Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.revenueByProperty.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No properties yet</p>
            ) : (
              <div className="space-y-4">
                {analytics.revenueByProperty.map((prop, i) => (
                  <div key={prop.property_id || prop.name || `prop-${i}`} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{prop.name}</span>
                      <Badge variant="outline">{prop.occupied}/{prop.units} occupied</Badge>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Revenue: {formatCurrency(prop.revenue)}</span>
                      <span>Occupancy: {prop.units > 0 ? ((prop.occupied / prop.units) * 100).toFixed(0) : 0}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-[#C85A42] rounded-full" style={{ width: `${prop.units > 0 ? (prop.occupied / prop.units) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-[#C85A42]" /> Status Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="units">
              <TabsList className="w-full">
                <TabsTrigger value="units" className="flex-1">Units</TabsTrigger>
                <TabsTrigger value="payments" className="flex-1">Payments</TabsTrigger>
                <TabsTrigger value="leases" className="flex-1">Leases</TabsTrigger>
              </TabsList>
              <TabsContent value="units" className="mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full" />Occupied</span>
                  <span className="font-semibold">{analytics.unitStatus.occupied}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-500 rounded-full" />Vacant</span>
                  <span className="font-semibold">{analytics.unitStatus.vacant}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full" />Maintenance</span>
                  <span className="font-semibold">{analytics.unitStatus.maintenance}</span>
                </div>
              </TabsContent>
              <TabsContent value="payments" className="mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full" />Verified</span>
                  <span className="font-semibold">{analytics.paymentStatus.verified}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full" />Pending</span>
                  <span className="font-semibold">{analytics.paymentStatus.pending}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full" />Rejected</span>
                  <span className="font-semibold">{analytics.paymentStatus.rejected}</span>
                </div>
              </TabsContent>
              <TabsContent value="leases" className="mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full" />Active</span>
                  <span className="font-semibold">{analytics.activeLeases}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full" />Pending</span>
                  <span className="font-semibold">{analytics.pendingLeases}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2"><div className="w-3 h-3 bg-gray-400 rounded-full" />Total</span>
                  <span className="font-semibold">{analyticsData.leases.length}</span>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
